import type { Route } from "@route-types/Section/+types/Section";
import PageArticleList from "@src/app/components/ArticleList/PageArticleList";
import FeatureCard from "@src/app/components/FeatureCard";
import SectionContainer from "@src/app/components/SectionContainer/SectionContainer";
import {
  parsePayload,
  useRouteResponse,
} from "@src/app/hooks/useRouteResponse";
import type { Section } from "@src/app/types/Data";
import type { ListingPageResponse } from "@src/app/types/response";
import { createMetaTags } from "@src/app/utils/createMetaTags";

import { fetchSectionPageData } from "./Section.server";

/**
 * @see https://docs.google.com/spreadsheets/d/1JENgXBy5uUzs-1M6LAjYCfFUGp6qBzWmx0qa4zB8PQg/edit?gid=1514826223#gid=1514826223
 */
const metaInfo: Record<
  Exclude<Section, "home" | "video">,
  {
    title: string;
    desc: string;
    ogTitle?: string;
    ogDesc?: string;
  }
> = {
  politics: {
    title: "Political News & Opinions with a China focus | ThinkChina",
    desc: "Politics - Get latest insights on China through the eyes of global thought leaders & journalists.",
    ogTitle: "Political News & Opinions with a China focus",
  },
  economy: {
    title: "Economy News & Opinions with a China focus | ThinkChina",
    desc: "Economy - Get latest insights on China through the eyes of global thought leaders & journalists.",
    ogTitle: "Economy News & Opinions with a China focus ",
  },
  technology: {
    title: "Technology News & Opinions with a China focus | ThinkChina",
    desc: "Technology - Get latest insights on China through the eyes of global thought leaders & journalists.",
    ogTitle: "Technology News & Opinions with a China focus ",
  },
  history: {
    title: "History Essays & Columns with a China focus | ThinkChina",
    desc: "History - Get interesting stories, insights and analysis on China's history.",
    ogTitle: "History Essays & Columns with a China focus ",
  },
  society: {
    title: "Society News & Opinions with a China focus | ThinkChina",
    desc: "Society - Get latest insights on China through the eyes of global thought leaders & journalists.",
    ogTitle: "Society News & Opinions with a China focus ",
  },
  culture: {
    title: "Cultural Essays & Columns with a China focus | ThinkChina",
    desc: "Culture - Get interesting stories, insights and analysis on China's culture.",
    ogTitle: "Cultural Essays & Columns with a China focus ",
  },
  cartoon: {
    title: "Cartoons with a China focus | ThinkChina",
    desc: "Cartoons - Get interesting cartoons on China and Chinese society.",
    ogTitle: "Cartoons with a China focus",
  },
};

export const loader = fetchSectionPageData;

export const meta = (args: Route.MetaArgs) => {
  const payload = parsePayload<ListingPageResponse>(args.loaderData);
  const section = payload.section || "";
  const defaultMetaInfo = {
    title: "",
    desc: "",
    ogTitle: "",
    ogDesc: "",
  };

  const pickedMetaInfo =
    section in metaInfo
      ? metaInfo[section as Exclude<Section, "home" | "video">]
      : defaultMetaInfo;
  return createMetaTags({
    title: pickedMetaInfo.title,
    description: pickedMetaInfo.desc,
    canonical: `https://www.thinkchina.sg/${section}`,
    ogInfo: {
      ogTitle: pickedMetaInfo.ogTitle,
      ogDescription: pickedMetaInfo.ogDesc,
    },
  });
};

export default function SectionPage() {
  const route = useRouteResponse("sectionPage");

  const heroArticle = route.heroArticles?.[0];
  const sectionName = route.sectionName || "";

  return (
    <div className="**:[overflow-anchor:none]">
      <SectionContainer title={sectionName}>
        <div>
          {heroArticle ? <FeatureCard article={heroArticle} /> : null}

          <PageArticleList
            showHeader={false}
            initialData={route.articles || []}
            title={sectionName}
            requestPath={`/page/${route.section}`}
            requestQuery={{
              excludeIds: route.heroArticles?.map((i) => i.id).join(",") || "",
            }}
          />
        </div>
      </SectionContainer>
    </div>
  );
}
